import { Box, Divider } from '@mui/material'
import * as c from 'common/c'
import {
  AccountCircle as UserIcon,
  People as OrganizationIcon,
} from '@mui/icons-material'

import { useTranslation } from 'react-i18next'
import { Navigate, Routes, Route } from 'react-router'
import OrganizationsComponent from './organizations.component'
import { AgProUsers, UserAccounts } from 'containers/users'
import { CommonSidebar, CommonSidebarLink } from 'components/common-sidebar'
import IntegrationsPanel from 'components/admin/integrations-panel'
import SubscriptionComponent from 'components/admin/subscription'
import UbjUploadsComponent from 'components/admin/ubj-uploads'
import TrxUploadsComponent from 'components/admin/trx-uploads'
import TrxStreamComponent from 'components/admin/trx-stream'
import BulkExport from 'components/admin/bulk-export'
import Partner from 'components/admin/partner'
import OrganizationRenewal from 'components/admin/organization-renewals'
import { useSelector } from 'react-redux'
import { useContext, useEffect } from 'react'
import { get } from 'es-toolkit/compat'
import {
  FarmtrxIconDiamond,
  FarmtrxIconFile,
  FarmtrxIconUsers,
  FarmtrxIconLinkIntegration,
  FarmtrxIconExportHarvest as CloudDownloadIcon,
} from '@farmtrx/farmtrx-icons'
import { getIntegrationToken } from 'api/integration'

import FeatureFlag from 'components/feature-flag'
import FeatureFlagsContext from 'context/feature-flags-context'
import { FEATURE_FLAGS } from 'feature-flags'
import { TEST_IDS } from 'test-ids'
import useLogLinkRequest from 'components/farm-settings/log-viewer/use-log-link-request'

export default function AdminComponent() {
  const {
    link: trxLinkRequest,
    tenantId: trxLinkTenantId,
    clearLink: clearTrxLink,
  } = useLogLinkRequest('trx')
  const permissions = useSelector((state) => state.account.permissions)
  const userData = useSelector((state) => state.account.user)
  const customClaims = useSelector((state) => state.account.customClaims)
  const showOrganizations =
    permissions.includes('tenant:list') && !userData.isAgPro
  const showPartners = permissions.includes('farmtrx:admin')
  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]

  const showOrganizationRenewals =
    (permissions.includes('partner:admin') ||
      customClaims?.[c.FT_PARTNER_CLAIM]) &&
    !showPartners
  const showAgProUsers = permissions.includes('users:write')
  const showAccounts = permissions.includes('tenant:list')
  const defaultRoute = showOrganizations
    ? '/admin/organizations'
    : '/admin/ag-pro-users'

  const features = useContext(FeatureFlagsContext)
  // If the feature flag is not found, it is disabled by default
  const orders = get(features, FEATURE_FLAGS.orders, { isEnabled: false })
  const trxUploads = get(features, FEATURE_FLAGS.trxUploads, {
    isEnabled: false,
  })

  useEffect(() => {
    const integrationLogin = localStorage.getItem('integrationLoginAttempt')
    if (integrationLogin) {
      getIntegrationToken(JSON.parse(integrationLogin), () => {}, true)
    }
  }, [])

  const { t } = useTranslation()
  return (
    <Box
      sx={{
        width: 1,
        height: 1,
        display: 'flex',
      }}
    >
      <CommonSidebar testId={TEST_IDS.adminSidebar}>
        {showOrganizations && (
          <CommonSidebarLink
            key="organizations"
            exact="true"
            to="../organizations"
            icon={<OrganizationIcon />}
            label={t('available_organizations')}
            testId={TEST_IDS.adminOrganizationsNav}
          />
        )}

        {showOrganizations && <Divider />}
        {showPartners && (
          <>
            <CommonSidebarLink
              key="partners"
              exact="true"
              to="../partners"
              icon={<OrganizationIcon />}
              label={orders.isEnabled ? t('partners_resellers') : t('partners')}
              testId={TEST_IDS.adminPartnersNav}
            />
            <Divider />
          </>
        )}
        {showOrganizationRenewals && (
          <>
            <CommonSidebarLink
              key="organization-renewals"
              exact="true"
              to="../organization-renewals"
              icon={<OrganizationIcon />}
              label={t('organization_renewals')}
              testId={TEST_IDS.adminOrganizationRenewalsNav}
            />
            <Divider />
          </>
        )}

        {showAgProUsers && (
          <CommonSidebarLink
            key="ag-pro-users"
            to="../ag-pro-users"
            icon={<FarmtrxIconUsers />}
            label={t('agpro_users')}
            testId={TEST_IDS.adminAgProUsersNav}
          />
        )}
        <Divider />
        <CommonSidebarLink
          key="subscriptions"
          to="../subscription"
          icon={<FarmtrxIconDiamond />}
          label={t('farmtrx_premium')}
          testId={TEST_IDS.adminSubscriptionsNav}
        />
        <Divider />
        {showAccounts && (
          <CommonSidebarLink
            key="accessible-accounts"
            to="../accessible-accounts"
            icon={<UserIcon />}
            label={t('accessible_accounts')}
            testId={TEST_IDS.adminAccessibleAccountsNav}
          />
        )}
        {showAccounts && <Divider />}
        <CommonSidebarLink
          key="ubj-uploads"
          to="../ubj-uploads"
          icon={<FarmtrxIconFile />}
          label={t('ubj_uploads')}
          testId={TEST_IDS.adminUbjUploadsNav}
        />

        <FeatureFlag featureName={FEATURE_FLAGS.trxUploads}>
          {showAccounts && <Divider />}
          <CommonSidebarLink
            key="trx-uploads"
            to="../trx-uploads"
            icon={<FarmtrxIconFile />}
            label={t('trx_uploads')}
            testId={TEST_IDS.adminTrxUploadsNav}
          />
          <Divider />
        </FeatureFlag>
        {isAdmin && (
          <FeatureFlag featureName={'trx_uploads'}>
            <CommonSidebarLink
              key="trx-stream"
              to="../trx-stream"
              icon={<FarmtrxIconFile />}
              label={t('trx_streams')}
              testId={TEST_IDS.adminTrxStreamNav}
            />
            <Divider />
          </FeatureFlag>
        )}

        <CommonSidebarLink
          key="integrations"
          to="../integrations"
          icon={<FarmtrxIconLinkIntegration />}
          label={t('integrations')}
          testId={TEST_IDS.adminIntegrationsNav}
        />

        <Divider />
        <CommonSidebarLink
          key="bulk-export"
          to="../bulk-export"
          icon={<CloudDownloadIcon />}
          label={t('bulk_export')}
          testId={TEST_IDS.adminBulkExportNav}
        />
        <Divider />
      </CommonSidebar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 6,
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <Routes>
          <Route
            exact
            path="/"
            element={<Navigate to={defaultRoute} replace={true} />}
          />
          {showOrganizations && (
            <Route path="/organizations" element={<OrganizationsComponent />} />
          )}
          {showPartners && <Route path="/partners" element={<Partner />} />}
          {showOrganizationRenewals && (
            <Route
              path="/organization-renewals"
              element={<OrganizationRenewal />}
            />
          )}
          <Route path="/accessible-accounts" element={<UserAccounts />} />
          <Route path="/ag-pro-users" element={<AgProUsers />} />
          <Route path="/integrations" element={<IntegrationsPanel />} />
          <Route path="/subscription" element={<SubscriptionComponent />} />
          <Route path="/ubj-uploads" element={<UbjUploadsComponent />} />

          {trxUploads.isEnabled && (
            <Route path="/trx-uploads" element={<TrxUploadsComponent />} />
          )}
          {isAdmin && trxUploads.isEnabled && (
            <Route
              path="/trx-stream"
              element={
                <TrxStreamComponent
                  linkRequest={trxLinkRequest}
                  linkTenantId={trxLinkTenantId}
                  clearLink={clearTrxLink}
                />
              }
            />
          )}
          <Route path="/bulk-export" element={<BulkExport />} />
        </Routes>
      </Box>
    </Box>
  )
}
